import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Trash2, Plus, ExternalLink, FileSpreadsheet, CheckCircle2, XCircle, ToggleLeft, ToggleRight } from 'lucide-react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const inputCls = "bg-[#0A1F1A] border border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] focus:ring-0 rounded-xl h-11 text-sm";

const fmtDT = (iso) => {
  if (!iso) return '-';
  try {
    return new Intl.DateTimeFormat('es-CO', { timeZone: 'America/Bogota', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  } catch { return iso; }
};

export default function SheetSyncs() {
  const [items, setItems] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState(null);
  const [form, setForm] = useState({ name: '', sheet_url: '', broker_id: '', tab_name: '' });
  const [availableTabs, setAvailableTabs] = useState([]);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [syncs, users] = await Promise.all([api.get('/sheet-syncs'), api.get('/users')]);
      setItems(syncs);
      setBrokers((users || []).filter(u => u.role === 'broker' || u.role === 'admin'));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al cargar'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const runSync = async (id) => {
    setSyncingId(id);
    try {
      const r = await api.post(`/sheet-syncs/${id}/run`);
      let msg = `Sync: ${r.created} nuevos, ${r.skipped_dup} duplicados, ${r.skipped_empty} vacios`;
      if (r.created_ids && r.created_ids.length) {
        msg += ' - ' + r.created_ids.slice(0, 3).map(x => x.name).join(', ');
      }
      toast.success(msg);
      if (r.errors && r.errors.length) toast.warning(r.errors[0]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al sincronizar'); }
    setSyncingId(null);
  };

  const createOne = async () => {
    if (!form.name.trim() || !form.sheet_url.trim() || !form.broker_id) {
      toast.error('Completa nombre, link y broker');
      return;
    }
    setCreating(true);
    try {
      await api.post('/sheet-syncs', form);
      toast.success('Fuente agregada. Se sincronizara cada 3 minutos.');
      setForm({ name: '', sheet_url: '', broker_id: '', tab_name: '' });
      setAvailableTabs([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al crear'); }
    setCreating(false);
  };

  const loadTabs = async (url) => {
    if (!url || !url.includes('/spreadsheets/')) { setAvailableTabs([]); return; }
    setLoadingTabs(true);
    try {
      const tabs = await api.get(`/sheet-syncs/tabs?sheet_url=${encodeURIComponent(url)}`);
      setAvailableTabs(tabs || []);
    } catch (e) { setAvailableTabs([]); toast.error(e?.response?.data?.detail || 'No se pudieron leer las pestañas'); }
    setLoadingTabs(false);
  };

  const updateTab = async (id, tab_name) => {
    try { await api.put(`/sheet-syncs/${id}`, { tab_name }); load(); toast.success('Pestaña actualizada'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminar esta fuente de sincronizacion?')) return;
    try { await api.delete(`/sheet-syncs/${id}`); load(); toast.success('Eliminada'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const resetAndResync = async (id) => {
    if (!window.confirm('Reset: la proxima sync re-evaluara todas las filas del Sheet. Los leads ya existentes se detectaran como duplicados (por meta_leadgen_id o telefono) y no se duplicaran. Continuar?')) return;
    try {
      await api.post(`/sheet-syncs/${id}/reset`);
      toast.success('Reset OK. Sincronizando...');
      await runSync(id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error en reset'); }
  };

  const toggleActive = async (it) => {
    try { await api.put(`/sheet-syncs/${it.id}`, { active: !it.active }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-6" data-testid="sheet-syncs-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#A38560]/10 border border-[#A38560]/20 flex items-center justify-center">
          <FileSpreadsheet size={20} className="text-[#A38560]" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="font-outfit text-3xl font-medium tracking-tight text-white">Sync Google Sheets</h1>
          <p className="text-xs text-white/40 mt-0.5 tracking-wide">Leads de Meta u otras fuentes se importan automaticamente cada 3 minutos</p>
        </div>
      </div>

      {/* Add form */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5 space-y-4">
        <h2 className="text-sm font-medium text-white/80 flex items-center gap-2">
          <Plus size={14} className="text-[#A38560]" /> Nueva fuente
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input data-testid="sync-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="Nombre (ej: Meta - Juanca)" className={inputCls} />
          <Input data-testid="sync-url" value={form.sheet_url}
            onChange={e => { setForm({ ...form, sheet_url: e.target.value, tab_name: '' }); }}
            onBlur={e => loadTabs(e.target.value)}
            placeholder="Link del Google Sheet" className={`${inputCls} md:col-span-2`} />
          <Select value={form.broker_id} onValueChange={v => setForm({ ...form, broker_id: v })}>
            <SelectTrigger data-testid="sync-broker" className={inputCls}>
              <SelectValue placeholder="Broker asignado..." />
            </SelectTrigger>
            <SelectContent className="bg-[#0A1F1A] border-white/10">
              {brokers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {availableTabs.length > 0 && (
          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/40 block mb-1.5">Pestaña del documento</label>
            <Select value={form.tab_name || (availableTabs[0]?.title || '')} onValueChange={v => setForm({ ...form, tab_name: v })}>
              <SelectTrigger data-testid="sync-tab" className={inputCls}>
                <SelectValue placeholder="Elige la pestaña..." />
              </SelectTrigger>
              <SelectContent className="bg-[#0A1F1A] border-white/10">
                {availableTabs.map(t => <SelectItem key={t.gid} value={t.title}>{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {loadingTabs && <p className="text-[11px] text-white/40">Leyendo pestañas...</p>}
        <button onClick={createOne} disabled={creating} data-testid="create-sync-btn"
          className="w-full md:w-auto px-6 py-2.5 bg-[#A38560] hover:bg-[#8B6F47] text-[#03110D] text-sm font-medium rounded-xl disabled:opacity-50">
          {creating ? 'Creando...' : 'Agregar fuente'}
        </button>
        <p className="text-[11px] text-white/40 leading-relaxed border-t border-white/5 pt-3">
          El Sheet debe estar compartido como <strong className="text-[#A38560]">Cualquier persona con el enlace - Lector</strong>.
          Las columnas se autodetectan: full_name/nombre, phone/telefono, email, id/lead_id, ad_name, form_name.
        </p>
      </div>

      {/* List */}
      <div className="space-y-3">
        {items.map(it => {
          const lr = it.last_result || {};
          const ok = it.last_synced_at && (!lr.errors || lr.errors.length === 0);
          return (
            <div key={it.id} className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-5" data-testid={`sync-${it.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <button onClick={() => toggleActive(it)} className={it.active ? 'text-emerald-400' : 'text-white/30'} title={it.active ? 'Activo' : 'Pausado'}>
                      {it.active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                    </button>
                    <h3 className="text-white font-medium text-sm truncate">{it.name}</h3>
                    <span className="text-[10px] px-2 py-0.5 bg-[#A38560]/10 text-[#A38560] rounded-full uppercase tracking-wider">{it.broker_name}</span>
                  </div>
                  <a href={it.sheet_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#A38560] hover:underline">
                    Ver Sheet <ExternalLink size={10} />
                  </a>
                  <p className="text-[11px] text-white/40 mt-1">
                    Pestaña: <span className="text-white/70">{it.tab_name || '(primera)'}</span>
                    <button onClick={async () => {
                      const t = window.prompt('Nombre EXACTO de la pestaña (deja vacio para usar la primera):', it.tab_name || '');
                      if (t !== null) updateTab(it.id, t.trim());
                    }} className="ml-2 text-[#A38560] hover:underline">Cambiar</button>
                  </p>
                </div>

                <div className="flex flex-col lg:items-end gap-1 lg:min-w-[200px]">
                  <span className="text-[11px] text-white/40">Ultima sync: {fmtDT(it.last_synced_at)}</span>
                  {it.last_synced_at && (
                    <span className={`text-[11px] flex items-center gap-1 ${ok ? 'text-emerald-400' : 'text-orange-400'}`}>
                      {ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                      {lr.created || 0} nuevos, {lr.skipped_dup || 0} dup, {lr.total_rows || 0} filas
                    </span>
                  )}
                  {lr.errors && lr.errors.length > 0 && (
                    <span className="text-[10px] text-red-400 max-w-[250px] truncate" title={lr.errors.join(' | ')}>! {lr.errors[0]}</span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button onClick={() => runSync(it.id)} disabled={syncingId === it.id} data-testid={`run-${it.id}`}
                    className="px-3 py-2 bg-[#A38560]/10 hover:bg-[#A38560]/20 border border-[#A38560]/20 text-[#A38560] rounded-lg text-xs flex items-center gap-2 disabled:opacity-50">
                    <RefreshCw size={12} className={syncingId === it.id ? 'animate-spin' : ''} />
                    Sincronizar
                  </button>
                  <button onClick={() => resetAndResync(it.id)} disabled={syncingId === it.id} data-testid={`reset-${it.id}`}
                    title="Reset: re-evalua todas las filas (no duplica)"
                    className="px-3 py-2 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/20 text-orange-300 rounded-lg text-xs">
                    Reset
                  </button>
                  <button onClick={() => remove(it.id)} className="p-2 text-white/30 hover:text-red-400 hover:bg-red-500/10 rounded-lg" data-testid={`del-${it.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {!loading && items.length === 0 && (
          <div className="text-center py-12 text-white/30 text-sm bg-[#0A1F1A] rounded-2xl border border-white/5">
            No hay fuentes configuradas. Agrega la primera arriba.
          </div>
        )}
      </div>
    </div>
  );
}
